
import { defineComponent, computed, ref, onMounted, ComponentPublicInstance } from "vue";
import NativeClient, { TitleInfo } from "@/client/native-client";
import router from "@/router";
import store from "@/store";
import { useApiErrorHandler } from "@/use/use-api-error-handler";
import { useApiErrorDialog } from "@/use/use-api-error-dialog";
import { useLog } from "@/use/use-log";
import { PAGE_ID } from "@/const/log-const";
import { ApiError, BeaconLogInfo, UpdatePageInfo } from "@/types/common";
import { COMMON_LIMIT_NUM } from "@/const/limit_const";
import { COMMON_MESSAGE_STRING } from "@/const/message_const";
import CommonUtil from "@/utils/common-utils";
import CommonDesignDialog from "@/components/common/CommonDesignDialog.vue";
import FridgeDetailsDrumPicker from "@/components/fridgeDetails/FridgeDetailsDrumPicker.vue";
import { 
  FridgeDetailsSettingsList, 
  FridgeDetailsSetSettingsList, 
  FRIDGE_DETAILS_COOLING_MODE, 
  FRIDGE_DETAILS_COOLING_MODE_DISPLAY_STRING 
} from "@/types/fridgeDetails";

export default defineComponent({
  name: "FridgeSettingsTimePage",
  components: { CommonDesignDialog, FridgeDetailsDrumPicker },
  setup() {
    const { setLogId, outputPageLogs } = useLog();

    // タイトル行の設定
    NativeClient.setTitle(new TitleInfo(false, "クイック操作ボタン設定"));
    CommonUtil.scroll({ top: 0, left: 0 });

    // 時間の保存
    let currentMinutes = 0;
    let currentSeconds = 0;

    // 表示更新のためのリアクティブ
    const updateDisplayKey = ref(0);
    const updateDisplay = () => {
      if (updateDisplayKey.value == 100) {
        updateDisplayKey.value = 0;
      } else {
        updateDisplayKey.value++;
      }
    };

    // モード取得
    const mode = router.currentRoute.value.query.mode;

    // 参照
    const textInput = ref<ComponentPublicInstance<HTMLInputElement>>();

    // 定数
    const listTitleText = "リストタイトル（9文字まで）";
    const operationModeText = "運転コース";
    const operationTimeText = "運転時間";
    const buttonText = "保存する";
    const textMaxLength = 9;

    // 現在の設定値（操作中）を取得する
    const getCurrentSettingItem = () => {
      const buffer: FridgeDetailsSetSettingsList | null = store.getters["fridgeDetails/getSettingsBuffer"];
      if (buffer != null && buffer.quick_button_setting_list != undefined) {
        for (const item of buffer.quick_button_setting_list) {
          if (item.mode == mode) {
            return item;
          }
        }
      }
      return null;
    };

    // 上限、下限値を取得するために、該当のモードの設定値を取得する
    const getSettingItem = () => {
      const settings: FridgeDetailsSettingsList | null = store.getters["fridgeDetails/getSettings"];
      if (settings != null) {
        for (const item of settings.quick_button_setting_list) {
          if (item.mode == mode) {
            return item;
          }
        }
      }
      return null;
    };
    const minutesLowerLimit = computed(() => {
      const item = getSettingItem();
      if (item != null) {
        return item.lower_limit;
      }
      return 0;
    });
    const minutesUpperLimit = computed(() => {
      const item = getSettingItem();
      if (item != null) {
        return item.upper_limit;
      }
      return 0;
    });
    const initialMinutes = computed(() => {
      const item = getCurrentSettingItem();
      if (item != null) {
        currentMinutes = item.time;
        return item.time;
      }
      return 0;
    });

    // 設定
    const currentName = computed(() => {
      const item = getCurrentSettingItem();
      if (item != null) {
        return item.name;
      }
      return "";
    });
    const modeText = computed(() => {
      let result = "";
      switch (mode) {
        case FRIDGE_DETAILS_COOLING_MODE.QUENCH:
          result = FRIDGE_DETAILS_COOLING_MODE_DISPLAY_STRING.QUENCH;
          break;
        case FRIDGE_DETAILS_COOLING_MODE.COLD:
          result = FRIDGE_DETAILS_COOLING_MODE_DISPLAY_STRING.COLD;
          break;
        case FRIDGE_DETAILS_COOLING_MODE.FROZEN:
          result = FRIDGE_DETAILS_COOLING_MODE_DISPLAY_STRING.FROZEN;
          break;
        default:
          break;
      }
      return result;
    });
    const pushCountText = computed(() => {
      let result = "";
      switch (mode) {
        case FRIDGE_DETAILS_COOLING_MODE.QUENCH:
          result = "（ボタン1回押し）";
          break;
        case FRIDGE_DETAILS_COOLING_MODE.COLD:
          result = "（ボタン2回押し）";
          break;
        case FRIDGE_DETAILS_COOLING_MODE.FROZEN:
          result = "（ボタン3回押し）";
          break;
        default:
          break;
      }
      return result;
    });

    // 秒有効
    const isValidSeconds = computed(() => {
      if (mode == FRIDGE_DETAILS_COOLING_MODE.QUENCH) {
        return true;
      }
      return false;
    });
    const secondsLowerLimit = computed(() => {
      const item = getSettingItem();
      if (item != null && item.lower_limit_second != undefined && item.lower_limit_second != null) {
        return item.lower_limit_second;
      }
      return 0;
    });
    const secondsUpperLimit = computed(() => {
      const item = getSettingItem();
      if (item != null && item.upper_limit_second != undefined && item.upper_limit_second != null) {
        return item.upper_limit_second;
      }
      return 0;
    });
    const initialSeconds = computed(() => {
      const item = getCurrentSettingItem();
      if (item != null && item.time_second != undefined && item.time_second != null) {
        currentSeconds = item.time_second;
        return item.time_second;
      }
      return 0;
    });

    // // 本ページのロード
    // onMounted(() => {
    // });

    // ピッカーで選択された値
    const selectTime = (minutes: number, seconds: number) => {
      currentMinutes = minutes;
      currentSeconds = seconds;
    };

    // 保存
    const clickStore = () => {
      NativeClient.sendDebugLog("FridgeSettingsTimePage clickStore inputname = " + textInput.value?.value);
      const settingBuffer: FridgeDetailsSetSettingsList | null = store.getters["fridgeDetails/getSettingsBuffer"];
      if (settingBuffer == null || settingBuffer.quick_button_setting_list == undefined || settingBuffer.quick_button_setting_list.length == 0) {
        return;
      }
      for (let i = 0; i < settingBuffer.quick_button_setting_list.length; i ++ ) {
        if (settingBuffer.quick_button_setting_list[i].mode == mode) {
          if (textInput.value != undefined && textInput.value.value.length > 0) {
            settingBuffer.quick_button_setting_list[i].name = textInput.value.value;
          }
          settingBuffer.quick_button_setting_list[i].time = currentMinutes;
          if (mode == FRIDGE_DETAILS_COOLING_MODE.QUENCH)  {
            settingBuffer.quick_button_setting_list[i].time_second = currentSeconds;
          }
          break;
        }
      }
      store.dispatch("fridgeDetails/updateSettingsBuffer", settingBuffer);

      router.go(-1);
    };

    //
    // APIリクエスト時のエラーハンドル
    //
    const {
      handleApiError,
      getApiErrorTitle,
      getApiErrorMessage
    } = useApiErrorHandler();
    const { closeDialog } = useApiErrorDialog();
    const typeApiError = ref(9);
    const retryButtonTitle = "リトライ";
    const okButtonTitle = "OK";
    // APIのエラー・ダイアログは、リトライが存在しないが、共通ダイアログのIFは残っているため、削除せず、そのまま置いておく
    const retryApiErrorHandler = () => {
      closeDialog();
    };
    const okApiErrorHandler = () => {
      closeDialog();
    };

    return {
      listTitleText,
      textInput,
      textMaxLength,
      currentName,
      operationModeText,
      modeText,
      isValidSeconds,
      pushCountText,
      operationTimeText,
      buttonText,
      clickStore,
      minutesLowerLimit,
      minutesUpperLimit,
      initialMinutes,
      secondsLowerLimit,
      secondsUpperLimit,
      initialSeconds,
      selectTime,
      updateDisplayKey,
      handleApiError,
      getApiErrorTitle,
      getApiErrorMessage,
      typeApiError,
      retryButtonTitle,
      okButtonTitle,
      retryApiErrorHandler,
      okApiErrorHandler
    };
  }
});
